import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  DeleteSubmissionFailure,
  DeleteSubmissionInput,
  DeleteSubmissionMutation,
} from "@/graphql/codegen/graphql"
import { DeleteSubmissionDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseDeletePriceChangeSubmissionReturn {
  deleteSubmission: (input: DeleteSubmissionInput) => Promise<boolean>
  isDeleting: boolean
}

export const useDeleteSubmission = (): UseDeletePriceChangeSubmissionReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<DeleteSubmissionMutation, { input: DeleteSubmissionInput }>(
    DeleteSubmissionDocument
  )

  const deleteSubmission = useCallback(
    async (input: DeleteSubmissionInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.deleteSubmission
      const hasError = error || output?.__typename === "DeleteSubmissionFailure"

      if (hasError) {
        const message = error?.message ?? (output as DeleteSubmissionFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }
      return true
    },
    [executeMutation, notify]
  )

  return { deleteSubmission, isDeleting: fetching }
}
